import _defineProperty from "@babel/runtime/helpers/defineProperty";
import { getFieldPropertiesByTarget } from '@wix/form-viewer/controller';
import { createOrderAndCharge, getCheckout, updateCheckout } from '@wix/ambassador-ecom-v1-checkout/http';
import { ambassadorWithHeaders, getHeaders } from '../../utils/ambassador.utils';
import { FORMS_TEMPLATE_IDS, FQDN, FormFieldTarget } from '../../utils/consts';
import { createClient } from '@wix/sdk/client';
import { checkout } from '@wix/ecom';
import { SPECS } from '../SPECS';
import { mapCheckoutSdkToCheckout } from '../../utils/mapCheckoutSdkToCheckout';
export class CheckoutService {
  constructor(httpClient, _controllerConfig, getCurrency) {
    this.httpClient = httpClient;
    _defineProperty(this, "requestWithHeaders", void 0);
    _defineProperty(this, "checkoutApi", void 0);
    _defineProperty(this, "useSdk", void 0);
    _defineProperty(this, "getApiBaseUrl", controllerConfig => {
      const isSSR = controllerConfig.wixCodeApi.window.rendering.env === 'backend';
      if (isSSR) {
        return '';
      }
      return location.host;
    });
    this.useSdk = _controllerConfig.essentials.experiments.enabled(SPECS.CheckoutFromSDKInExpressCheckoutButtons);
    this.requestWithHeaders = () => ambassadorWithHeaders(httpClient, _controllerConfig, getCurrency());
    if (this.useSdk) {
      const wixClient = createClient({
        auth: {
          getAuthHeaders: () => ({
            headers: {
              Authorization: _controllerConfig.appParams.instance,
              ...getHeaders(_controllerConfig, getCurrency())
            }
          })
        },
        headers: {},
        modules: {
          checkout: {
            getCheckout: checkout.getCheckout,
            updateCheckout: checkout.updateCheckout
          }
        },
        host: {
          apiBaseUrl: this.getApiBaseUrl(_controllerConfig)
        }
      });
      this.checkoutApi = wixClient.checkout;
    }
  }
  async getCheckoutByCheckoutId(checkoutId) {
    if (this.useSdk) {
      var _this$checkoutApi;
      const response = await ((_this$checkoutApi = this.checkoutApi) == null ? void 0 : _this$checkoutApi.getCheckout(checkoutId));
      return mapCheckoutSdkToCheckout(response);
    }
    const {
      data
    } = await this.requestWithHeaders()(getCheckout({
      id: checkoutId
    }));
    return data.checkout;
  }
  async doesFormHaveRequiredDataExtendedFields() {
    const fieldProps = await getFieldPropertiesByTarget({
      options: {
        fqdn: FQDN
      },
      httpClient: this.httpClient,
      formId: FORMS_TEMPLATE_IDS.CUSTOMER_DETAILS_WO_ADDITIONAL_INFO
    });
    const fieldValues = fieldProps ? Object.values(fieldProps) : [];
    return fieldValues.filter(fieldValue => fieldValue == null ? void 0 : fieldValue.required).length > 0;
  }
  async doesFormHaveRequiredAdditionalInfoField() {
    var _fieldProps$FormField;
    const fieldProps = await getFieldPropertiesByTarget({
      httpClient: this.httpClient,
      formId: FORMS_TEMPLATE_IDS.CUSTOMER_DETAILS_WO_ADDITIONAL_INFO
    });
    return Boolean(fieldProps && ((_fieldProps$FormField = fieldProps[FormFieldTarget.additional_info_1]) == null ? void 0 : _fieldProps$FormField.required));
  }
  async createOrderAndCharge(_ref) {
    let {
      id,
      paymentToken,
      urlParams
    } = _ref;
    let orderId;
    let paymentResponseToken;
    try {
      const request = {
        id,
        paymentToken,
        urlParams
      };
      const {
        data
      } = await this.requestWithHeaders()(createOrderAndCharge(request));
      orderId = data.orderId;
      paymentResponseToken = data.paymentResponseToken;
    } catch {
      orderId = undefined;
      paymentResponseToken = undefined;
    }
    return {
      orderId,
      paymentResponseToken
    };
  }
  async updateCheckoutForShippingEstimation(checkoutId, address, shouldSendFullAddressOnExpressButtons) {
    if (this.useSdk) {
      var _this$checkoutApi2;
      const response = await ((_this$checkoutApi2 = this.checkoutApi) == null ? void 0 : _this$checkoutApi2.updateCheckout(checkoutId, {
        shippingInfo: {
          shippingDestination: {
            address: {
              country: (address == null ? void 0 : address.country) ?? null,
              city: (address == null ? void 0 : address.city) ?? null,
              postalCode: (address == null ? void 0 : address.postalCode) ?? null,
              subdivision: (address == null ? void 0 : address.subdivision) ?? null,
              streetAddress: null,
              addressLine1: null,
              addressLine2: null
            }
          }
        }
      }));
      return mapCheckoutSdkToCheckout(response);
    }
    const {
      data
    } = await this.requestWithHeaders()(shouldSendFullAddressOnExpressButtons ? updateCheckout({
      checkout: {
        id: checkoutId,
        shippingInfo: {
          shippingDestination: {
            address
          }
        }
      },
      fieldMask: ['shippingInfo.shippingDestination.address']
    }) : updateCheckout({
      checkout: {
        id: checkoutId,
        shippingInfo: {
          shippingDestination: {
            address
          }
        }
      },
      fieldMask: ['shippingInfo.shippingDestination.address.country', 'shippingInfo.shippingDestination.address.postalCode', 'shippingInfo.shippingDestination.address.subdivision']
    }));
    return data.checkout;
  }
  async updateCheckoutByShippingContacts(checkoutId, shouldUpdateShippingAddress, billingContact, shippingContact) {
    var _shippingContact$name3, _shippingContact$name4, _shippingContact$name5, _shippingContact$name6;
    if (this.useSdk) {
      var _this$checkoutApi3, _shippingContact$name, _shippingContact$name2;
      const response = await ((_this$checkoutApi3 = this.checkoutApi) == null ? void 0 : _this$checkoutApi3.updateCheckout(checkoutId, {
        buyerInfo: {
          email: billingContact == null ? void 0 : billingContact.email
        },
        shippingInfo: {
          shippingDestination: {
            ...(shouldUpdateShippingAddress && {
              address: {
                country: (shippingContact == null ? void 0 : shippingContact.country) ?? null,
                subdivision: (shippingContact == null ? void 0 : shippingContact.subdivision) ?? null,
                postalCode: (shippingContact == null ? void 0 : shippingContact.zipCode) ?? null,
                city: (shippingContact == null ? void 0 : shippingContact.city) ?? null,
                streetAddress: null,
                addressLine1: (shippingContact == null ? void 0 : shippingContact.addressLine1) ?? null,
                addressLine2: (shippingContact == null ? void 0 : shippingContact.addressLine2) ?? null
              }
            }),
            contactDetails: {
              firstName: (shippingContact == null || (_shippingContact$name = shippingContact.name) == null ? void 0 : _shippingContact$name.firstName) ?? null,
              lastName: (shippingContact == null || (_shippingContact$name2 = shippingContact.name) == null ? void 0 : _shippingContact$name2.lastName) ?? null,
              phone: (shippingContact == null ? void 0 : shippingContact.phoneNumber) ?? null,
              company: null,
              vatId: null
            }
          }
        }
      }));
      return mapCheckoutSdkToCheckout(response);
    }
    const {
      data
    } = await this.requestWithHeaders()(updateCheckout({
      checkout: {
        id: checkoutId,
        buyerInfo: {
          email: billingContact == null ? void 0 : billingContact.email
        },
        ...(shouldUpdateShippingAddress && {
          shippingInfo: {
            shippingDestination: {
              address: {
                country: shippingContact == null ? void 0 : shippingContact.country,
                subdivision: shippingContact == null ? void 0 : shippingContact.subdivision,
                postalCode: shippingContact == null ? void 0 : shippingContact.zipCode,
                city: shippingContact == null ? void 0 : shippingContact.city,
                addressLine: shippingContact == null ? void 0 : shippingContact.addressLine1,
                addressLine2: shippingContact == null ? void 0 : shippingContact.addressLine2
              },
              contactDetails: {
                firstName: shippingContact == null || (_shippingContact$name3 = shippingContact.name) == null ? void 0 : _shippingContact$name3.firstName,
                lastName: shippingContact == null || (_shippingContact$name4 = shippingContact.name) == null ? void 0 : _shippingContact$name4.lastName,
                phone: (shippingContact == null ? void 0 : shippingContact.phoneNumber) || undefined
              }
            }
          }
        }),
        ...(!shouldUpdateShippingAddress && {
          shippingInfo: {
            shippingDestination: {
              contactDetails: {
                firstName: shippingContact == null || (_shippingContact$name5 = shippingContact.name) == null ? void 0 : _shippingContact$name5.firstName,
                lastName: shippingContact == null || (_shippingContact$name6 = shippingContact.name) == null ? void 0 : _shippingContact$name6.lastName,
                phone: (shippingContact == null ? void 0 : shippingContact.phoneNumber) || undefined
              }
            }
          }
        })
      },
      fieldMask: ['buyerInfo.email', ...(shouldUpdateShippingAddress ? ['shippingInfo.shippingDestination'] : []), ...(!shouldUpdateShippingAddress ? ['shippingInfo.shippingDestination.contactDetails'] : [])]
    }));
    return data.checkout;
  }
  async updateCheckoutByBillingContacts(checkoutId, billingContact) {
    if (this.useSdk) {
      var _this$checkoutApi4;
      const response = await ((_this$checkoutApi4 = this.checkoutApi) == null ? void 0 : _this$checkoutApi4.updateCheckout(checkoutId, {
        billingInfo: {
          address: {
            country: (billingContact == null ? void 0 : billingContact.countryCode) ?? null,
            postalCode: (billingContact == null ? void 0 : billingContact.postalCode) ?? null,
            city: (billingContact == null ? void 0 : billingContact.city) ?? null,
            subdivision: (billingContact == null ? void 0 : billingContact.state) ?? null,
            streetAddress: null,
            addressLine1: null,
            addressLine2: null
          },
          contactDetails: {
            firstName: (billingContact == null ? void 0 : billingContact.firstName) ?? null,
            lastName: (billingContact == null ? void 0 : billingContact.lastName) ?? null,
            phone: (billingContact == null ? void 0 : billingContact.phone) ?? null,
            company: null,
            vatId: null
          }
        }
      }));
      return mapCheckoutSdkToCheckout(response);
    }
    const {
      data
    } = await this.requestWithHeaders()(updateCheckout({
      checkout: {
        id: checkoutId,
        billingInfo: {
          address: {
            country: billingContact == null ? void 0 : billingContact.countryCode,
            postalCode: billingContact == null ? void 0 : billingContact.postalCode,
            city: billingContact == null ? void 0 : billingContact.city,
            subdivision: billingContact == null ? void 0 : billingContact.state
          },
          contactDetails: {
            firstName: billingContact == null ? void 0 : billingContact.firstName,
            lastName: billingContact == null ? void 0 : billingContact.lastName,
            phone: billingContact == null ? void 0 : billingContact.phone
          }
        }
      },
      fieldMask: ['billingInfo']
    }));
    return data.checkout;
  }
  async setShippingOption(checkoutId, shippingOptionId) {
    if (this.useSdk) {
      var _this$checkoutApi5;
      const response = await ((_this$checkoutApi5 = this.checkoutApi) == null ? void 0 : _this$checkoutApi5.updateCheckout(checkoutId, {
        shippingInfo: {
          selectedCarrierServiceOption: {
            code: shippingOptionId
          }
        }
      }));
      return mapCheckoutSdkToCheckout(response);
    }
    const {
      data
    } = await this.requestWithHeaders()(updateCheckout({
      checkout: {
        id: checkoutId,
        shippingInfo: {
          selectedCarrierServiceOption: {
            code: shippingOptionId
          }
        }
      },
      fieldMask: ['shippingInfo.selectedCarrierServiceOption']
    }));
    return data.checkout;
  }
}
//# sourceMappingURL=CheckoutService.js.map